import {defineStore} from 'pinia';
import ApiService from '@core/services/ApiService';
import AuthService from '@core/services/AuthService';
import httpService from '@core/services/AxiosApiService';
import {isUnauthorizedError} from '@core/utils/isUnauthorizedError';
import {useApiProductStore} from '@internalDevportal/store/Environment-ApiProductStore';
import {useApplicationStore} from '@internalDevportal/store/Environment-ApplicationStore';
import {useSpecStore} from '@internalDevportal/store/SpecStore';
import {SavedOptions} from '@core/helpers/persistOptionsHelper';
import {ref} from 'vue';
import {useAdminStore} from '@core/store/AdminStore';
import apigeeEdgeLogo from '@assets/apigee-edge-logo.svg';
import apigeeXLogo from '@assets/apigee-x-logo.svg';
import apigeeLogoSmall from '@assets/apigee-logo-small.svg';
import azureLogo from '@assets/azure-logo.png';
import graviteeLogoSmall from '@assets/gravitee-logo-small.jpeg';

export interface Environment {
  id?: string;
  name: string;
  displayName: string;
  url: string;
  devPortalUrl?: string;
  tokenUrl?: string | null;
  wifAudience?: string | null;
  organization?: string;
  platformId?: string;
  platform?: Platform;
  platformUser?: User | null;
  devportalUser?: User;
  devportalUserId?: string;
  platformUserId?: string;
  platformUserCredentials?: {username: string; password: string};
  subscriptionId?: string;
  selectedAuthenticationMethod?: string;
  tenantId?: string;
  resourceGroupName?: string;
  createdAt?: string;
  updatedAt?: string;
  accessControl?: EnvironmentAccessControl | null;
}

export interface EnvironmentAccessControl {
  entryRole: string;
  rolePrefix?: string;
}

export type EnvironmentAccessStatus = 'allowed' | 'forbidden';

export type EnvironmentAccessRecheckResult = 'allowed' | 'forbidden' | 'failed';

export type PlatformType = 'Apigee' | 'Azure' | 'Gravitee' | 'AWS';
export type PlatformTypeValue = PlatformType | (string & Record<string, never>);

interface Platform {
  type: PlatformTypeValue;
}

export interface SupportedPlatform {
  id?: string;
  key: PlatformType;
  description: string;
  logo: string;
  url?: string;
  tokenUrl?: string;
}

export interface User {
  id?: string;
  username: string;
  password?: string;
  updatePassword?: boolean;
  devportalEnvironments?: Environment[];
  environments?: Environment[];
}

/** Persisted / API values for Azure environment auth (legacy spelling "Principle"). */
export const ENVIRONMENT_AUTH_SAS_TOKEN = 'SAS Token';
export const ENVIRONMENT_AUTH_AZURE_AD_PRINCIPLE = 'AzureAD Principle';

/** Strip `http(s)://` for display/edit parity with API-normalized URLs. */
export function stripEnvironmentUrlProtocol(s: string): string {
  return (s || '').replace(/^https?:\/\//i, '').trim();
}

function normalizeEnvironmentUrls(env: Environment): Environment {
  return {
    ...env,
    url: stripEnvironmentUrlProtocol(env.url || ''),
    ...(env.devPortalUrl != null && {
      devPortalUrl: stripEnvironmentUrlProtocol(env.devPortalUrl),
    }),
    ...(env.tokenUrl != null && {
      tokenUrl: stripEnvironmentUrlProtocol(env.tokenUrl),
    }),
  };
}

function emptyEnvironment(): Environment {
  return {
    name: '',
    displayName: '',
    url: '',
  };
}

/** Canonical stored prefix always ends with `:` (IDP role separator). */
export function normalizeRolePrefix(rolePrefix: string): string {
  const trimmed = rolePrefix.trim();
  if (!trimmed) return '';
  return trimmed.endsWith(':') ? trimmed : `${trimmed}:`;
}

/** Strip trailing `:` for display in admin inputs. */
export function formatRolePrefixForInput(rolePrefix: string): string {
  const trimmed = rolePrefix.trim();
  if (!trimmed) return '';
  return trimmed.endsWith(':') ? trimmed.slice(0, -1) : trimmed;
}

export type EnvironmentFormPayload = Environment & {
  useWorkloadIdentityFederation?: boolean;
};

/** Enforce mutually exclusive Apigee auth fields before create/update. */
export function normalizeEnvironmentAuthPayload(
  env: EnvironmentFormPayload,
): Environment {
  const {useWorkloadIdentityFederation, ...payload} = env;
  const isApigee = payload.platform?.type === 'Apigee';

  if (!isApigee) {
    return payload;
  }

  if (useWorkloadIdentityFederation) {
    return {
      ...payload,
      wifAudience: payload.wifAudience?.trim() || null,
      tokenUrl: null,
      platformUser: null,
    };
  }

  return {
    ...payload,
    wifAudience: null,
    tokenUrl: payload.tokenUrl || null,
    platformUser: payload.platformUser ?? null,
  };
}

export function normalizeEnvironmentAccessControlPayload(
  env: Environment,
): Environment {
  const accessControl = env.accessControl;
  const entryRole = accessControl?.entryRole?.trim() ?? '';
  const rolePrefix = normalizeRolePrefix(accessControl?.rolePrefix ?? '');
  const normalized = {...env};

  if (!entryRole) {
    normalized.accessControl = null;
    return normalized;
  }

  normalized.accessControl = {
    entryRole,
    ...(rolePrefix ? {rolePrefix} : {}),
  };
  return normalized;
}

export const useEnvironmentStore = defineStore(
  'EnvironmentStore',
  () => {
    const api = ApiService.getApiInstance();
    const adminStore = useAdminStore();
    const apiProductStore = useApiProductStore();
    const applicationStore = useApplicationStore();
    const specStore = useSpecStore();

    const environments = ref<Environment[]>([]);
    const currentEnvironment = ref<Environment>({
      name: '',
      displayName: '',
      url: '',
    });
    const environmentDetails = ref<Environment>({
      name: '',
      displayName: '',
      url: '',
    });
    const authenticationMethods = ref<string[]>([
      ENVIRONMENT_AUTH_SAS_TOKEN,
      ENVIRONMENT_AUTH_AZURE_AD_PRINCIPLE,
    ]);
    const supportedPlatforms = ref<SupportedPlatform[]>([
      {
        key: 'Apigee',
        description: 'Apigee Edge',
        logo: apigeeEdgeLogo,
        url: 'https://api.enterprise.apigee.com',
      },
      {
        key: 'Apigee',
        description: 'Apigee X',
        logo: apigeeXLogo,
        url: 'https://apigee.googleapis.com',
        tokenUrl: 'https://oauth2.googleapis.com',
      },
      {
        key: 'Apigee',
        description: 'Apigee On Prem',
        logo: apigeeLogoSmall,
      },
      {
        key: 'Azure',
        description: 'Azure',
        logo: azureLogo,
      },
      {
        key: 'Gravitee',
        description: 'Gravitee',
        logo: graviteeLogoSmall,
      },
    ]);
    const platforms = ref<unknown[]>([]);
    const loadingPlatforms = ref<boolean>(false);
    const users = ref<User[]>([]);
    const devportalUsers = ref<unknown[]>([]);
    const loadingEnvironments = ref<boolean>(false);
    const noAccessibleEnvironments = ref<boolean>(false);
    const environmentsAccessCheckFailed = ref<boolean>(false);
    const hasNoEnvironmentsSetup = ref<boolean>(false);
    // True once getEnvironments has completed a determination at least once.
    // Distinguishes "not loaded yet" from "loaded and confirmed no access",
    // so the no-environment-access guard never fires on the empty initial state.
    const environmentsChecked = ref<boolean>(false);
    const environmentAccessStatus = ref<EnvironmentAccessStatus>('allowed');
    const viewMode = ref<'list' | 'cards'>('list');
    const envOptions = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });

    function doesCurrentEnvSupportAppFiltering() {
      return (
        currentEnvironment.value?.platform?.type === 'Gravitee' ||
        currentEnvironment.value?.platform?.type === 'Azure'
      );
    }

    function doesCurrentEnvSupportExportCompanyAnalytics() {
      return currentEnvironment.value?.platform?.type === 'Gravitee';
    }

    function hasNoEnvironmentAccessState() {
      return (
        noAccessibleEnvironments.value ||
        (environments.value.length === 0 && !hasNoEnvironmentsSetup.value)
      );
    }

    async function getEnvironments(): Promise<boolean> {
      loadingEnvironments.value = true;
      environmentsAccessCheckFailed.value = false;
      try {
        const response = await api.get(
          '/environments?filter={"include":["platform"]}',
        );
        const normalized = (response.data as Environment[]).map(
          normalizeEnvironmentUrls,
        );
        environments.value = normalized;
        if (normalized.length === 0) {
          currentEnvironment.value = emptyEnvironment();
          try {
            const countResponse = await api.get('/environments/count');
            hasNoEnvironmentsSetup.value = countResponse.data.count === 0;
          } catch (error) {
            if (isUnauthorizedError(error)) {
              environmentsAccessCheckFailed.value = true;
              return false;
            }
            environmentsAccessCheckFailed.value = true;
            return false;
          }
          if (!environmentsAccessCheckFailed.value) {
            noAccessibleEnvironments.value = !hasNoEnvironmentsSetup.value;
          }
          return true;
        }

        hasNoEnvironmentsSetup.value = false;
        noAccessibleEnvironments.value = false;
        const currentEnvironmentIsAccessible = normalized.some(
          environment => environment.id === currentEnvironment.value?.id,
        );
        if (
          currentEnvironment.value?.displayName === '' ||
          normalized.length === 1 ||
          !currentEnvironmentIsAccessible
        ) {
          await changeEnvironment(normalized[0]);
        }
        return true;
      } catch {
        environmentsAccessCheckFailed.value = true;
        return false;
      } finally {
        loadingEnvironments.value = false;
        // A failed (401) check is inconclusive; only mark as checked when we
        // actually determined the access state.
        if (!environmentsAccessCheckFailed.value) {
          environmentsChecked.value = true;
        }
      }
    }

    async function recheckEnvironmentAccess(): Promise<EnvironmentAccessRecheckResult> {
      environmentsAccessCheckFailed.value = false;
      const user = await AuthService.ensureValidUser();
      if (!user?.access_token) {
        environmentsAccessCheckFailed.value = true;
        return 'failed';
      }
      httpService.setAccessToken(user.access_token);
      const loaded = await getEnvironments();
      if (!loaded || environmentsAccessCheckFailed.value) {
        return 'failed';
      }
      return hasNoEnvironmentAccessState() ? 'forbidden' : 'allowed';
    }

    async function getEnvironmentDetails(id: string) {
      const response = await api.get(
        `/environments/${id}?filter={"include":["platform"]}`,
      );
      environmentDetails.value = normalizeEnvironmentUrls(response.data);
    }

    async function editEnvironment(
      environmentUpdates: EnvironmentFormPayload,
    ): Promise<boolean> {
      loadingEnvironments.value = true;
      try {
        const {id} = environmentDetails.value;
        const {platform: _, ...updates} =
          normalizeEnvironmentAccessControlPayload(
            normalizeEnvironmentAuthPayload(environmentUpdates),
          );
        const updated = await api.put(`/environments/${id}`, updates);
        if (!updated || !id) {
          return false;
        }
        await getEnvironmentDetails(id);
        return true;
      } finally {
        loadingEnvironments.value = false;
      }
    }

    async function createEnvironment(
      environmentPayload: EnvironmentFormPayload,
      selectedPlatform: SupportedPlatform,
    ): Promise<any> {
      loadingEnvironments.value = true;
      try {
        const platformResponse = await api.get(
          `/platforms?filter={"where":{ "type": "${selectedPlatform.key}"}}`,
        );
        if (platformResponse.data.length === 0) {
          const createPlatformResponse = await api.post('/platforms', {
            type: selectedPlatform.key,
          });
          environmentPayload.platformId = createPlatformResponse.data.id;
        } else {
          environmentPayload.platformId = platformResponse.data[0].id;
        }
        const env = await api.post(
          '/environments',
          normalizeEnvironmentAccessControlPayload(
            normalizeEnvironmentAuthPayload({
              ...environmentPayload,
              platform: {type: selectedPlatform.key},
            }),
          ),
        );
        if (!env) {
          return false;
        }
        await getEnvironments();
        return env.data;
      } finally {
        loadingEnvironments.value = false;
      }
    }

    async function changeEnvironment(environment: Environment) {
      if (!environment.id) {
        currentEnvironment.value = emptyEnvironment();
        noAccessibleEnvironments.value = true;
        return;
      }
      const accessResponse = await api.get(
        `/environments/${environment.id}/access/check`,
      );
      environmentAccessStatus.value = accessResponse.data.status;
      if (environmentAccessStatus.value === 'forbidden') {
        currentEnvironment.value = emptyEnvironment();
        noAccessibleEnvironments.value = true;
        return;
      }
      currentEnvironment.value = normalizeEnvironmentUrls(environment);
      await adminStore.getRoles(environment.id);
      applicationStore.$reset();
      const results = await Promise.allSettled([
        applicationStore.getApplications(undefined, ''),
        apiProductStore.getApiproducts(),
        apiProductStore.getCatProducts(10),
        specStore.getSpecs(),
        specStore.getLatestSpecs(),
        specStore.getLastUpdatedSpecs(),
      ]);
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        console.error('changeEnvironment: some calls failed', failures);
      }
    }

    async function deleteEnvironment(id: string): Promise<boolean> {
      await api.delete(`/environments/${id}`);
      return true;
    }

    function $reset() {
      environments.value = [];
      currentEnvironment.value = emptyEnvironment();
      environmentDetails.value = emptyEnvironment();
      loadingEnvironments.value = false;
      noAccessibleEnvironments.value = false;
      hasNoEnvironmentsSetup.value = false;
      environmentsAccessCheckFailed.value = false;
      environmentsChecked.value = false;
      environmentAccessStatus.value = 'allowed';
    }

    return {
      environments,
      currentEnvironment,
      environmentDetails,
      authenticationMethods,
      supportedPlatforms,
      platforms,
      loadingPlatforms,
      users,
      devportalUsers,
      loadingEnvironments,
      noAccessibleEnvironments,
      environmentsAccessCheckFailed,
      hasNoEnvironmentsSetup,
      environmentsChecked,
      environmentAccessStatus,
      viewMode,
      envOptions,
      doesCurrentEnvSupportAppFiltering,
      doesCurrentEnvSupportExportCompanyAnalytics,
      getEnvironments,
      recheckEnvironmentAccess,
      getEnvironmentDetails,
      editEnvironment,
      createEnvironment,
      changeEnvironment,
      deleteEnvironment,
      $reset,
    };
  },
  {
    persist: {
      // Keep the selected environment for hard refresh UX; the environment list
      // is still fetched per session so IDP access checks stay accurate.
      pick: ['currentEnvironment', 'envOptions', 'environments', 'viewMode'],
    },
  },
);
